// Meta kırılımları — ilgili Meta Ads sayfalarına yerleştirilen kartlar:
// Kitle: yaş × cinsiyet, bölge, ülke · Yerleşimler: cihaz · Kreatifler: biçim, ana metin, başlık ·
// Saatlik Performans sayfası. Backend: GET /analysis/meta/{clientId}/breakdown (yalnızca okuma).
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { T, MONO } from '../poyais-ui/theme';
import { useLang, addTranslations } from '../i18n';
import { Panel, Segments, DataTable, Col, NameCell, ShareCell, Loading, Muted, money, num, pct, roasTxt, roasColor, useMobile } from './ui';

addTranslations({
  'Segment': 'Segment', 'Pay': 'Share', 'Satın alma değeri': 'Purchase value', 'Link tıklaması': 'Link clicks', 'Potansiyel müşteri': 'Leads',
  'Boş hücre: Meta bu segment için o metriği vermedi; sıfır anlamına gelmez. Satın alma, potansiyel müşteri ve link tıklaması ayrı sonuçlardır, toplanmaz.':
    'Empty cell: Meta did not report that metric for this segment; it does not mean zero. Purchases, leads and link clicks are separate results and are not added up.',
  'Kadın': 'Female', 'Erkek': 'Male', 'Bilinmiyor': 'Unknown', 'Masaüstü': 'Desktop', 'Android telefon': 'Android phone', 'Android tablet': 'Android tablet', 'Diğer': 'Other',
  'Yaş ve Cinsiyet': 'Age and gender', 'Her hücre o yaş ve cinsiyet grubunun harcamasını ve ROAS’ını gösterir.': 'Each cell shows spend and ROAS for that age and gender group.',
  'Yaş': 'Age', 'Bölgeler': 'Regions', 'Reklamların gösterildiği il ve bölgeler.': 'Provinces and regions where ads were shown.',
  'Ülkeler': 'Countries', 'Cihazlar': 'Devices', 'Reklamların gösterildiği cihazlar.': 'Devices on which ads were shown.',
  'Reklam Biçimleri': 'Ad formats', 'Biçim, reklamın kreatifine göre sınıflandırılır.': 'Format is classified from the ad’s creative.', 'Reklam sayısı': 'Ads',
  'Ana Metinler': 'Primary texts', 'Başlıklar': 'Headlines',
  'Bu kırılım yalnızca birden çok metin seçeneği kullanan reklamlarda (dinamik kreatif / Advantage+) oluşur.': 'This breakdown only exists for ads that use several text options (dynamic creative / Advantage+).',
  'Saatlik Performans': 'Hourly performance', 'Saat, reklam hesabının saat dilimine göredir.': 'Hours are in the ad account’s time zone.',
  'Harcamanın En Yüksek Olduğu Saatler': 'Highest-spend hours', 'En İyi ROAS Saatleri': 'Best ROAS hours', 'Saat': 'Hour', 'Bu kırılım açılamadı.': 'This breakdown could not be loaded.',
  'En az ₺500 harcanan saatler arasında.': 'Among hours with at least ₺500 spend.', 'En az ₺500 harcanan günler arasında.': 'Among days with at least ₺500 spend.',
  'Harcamanın En Yüksek Olduğu Günler': 'Highest-Spend Days', 'En İyi ROAS Günleri': 'Best ROAS Days', 'Günlük Performans': 'Daily Performance', 'Saatlik': 'Hourly', 'Günlük': 'Daily', 'Gün': 'Day',
  'Seçili dönemdeki günler haftanın gününe göre toplanır.': 'Days in the selected period are grouped by day of week.', 'Pazartesi': 'Monday', 'Salı': 'Tuesday', 'Çarşamba': 'Wednesday', 'Perşembe': 'Thursday', 'Cuma': 'Friday', 'Cumartesi': 'Saturday', 'Pazar': 'Sunday', 'satın alma': 'purchases', 'Satın alma yok': 'No purchases',
  'Meta bu kırılımda satın alma verisi döndürmüyor; bu yüzden satın alma ve ROAS sütunları gösterilmiyor.': 'Meta returns no purchase data for this breakdown, so purchase and ROAS columns are hidden.',
});

const API = '/api/v1';

function useBreakdown(clientId: number, dim: string, start: string, end: string) {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setD(null); setErr('');
    axios.get(`${API}/analysis/meta/${clientId}/breakdown?dimension=${dim}&limit=60&start_date=${start}&end_date=${end}`)
      .then(r => { if (alive) setD(r.data); }).catch(e => { if (alive) setErr(e?.response?.data?.detail || 'err'); });
    return () => { alive = false; };
  }, [clientId, dim, start, end]);
  return { d, err };
}

const DEVICE: Record<string, string> = { iphone: 'iPhone', ipad: 'iPad', ipod: 'iPod', android_smartphone: 'Android telefon', android_tablet: 'Android tablet', desktop: 'Masaüstü', other: 'Diğer', unknown: 'Bilinmiyor' };
const GENDER: Record<string, string> = { female: 'Kadın', male: 'Erkek', unknown: 'Bilinmiyor' };
let regionNames: Intl.DisplayNames | null = null;
try { regionNames = new Intl.DisplayNames(['tr'], { type: 'region' }); } catch { regionNames = null; }

export function segLabel(dim: string, raw: string, t: (s: string) => string) {
  const s = String(raw ?? '');
  if (dim === 'device') return t(DEVICE[s] || s);
  if (dim === 'country') return s === 'unknown' ? t('Bilinmiyor') : (regionNames?.of(s.toUpperCase()) || s);
  if (dim === 'region') {
    const x = s.replace(/ (Province|Region|District|County)$/i, '').replace(/^Istanbul$/, 'İstanbul').replace(/^Izmir$/, 'İzmir');
    return x === 'Unknown' || x === 'unknown' ? t('Bilinmiyor') : x;
  }
  return s;
}

const NOTE = 'Boş hücre: Meta bu segment için o metriği vermedi; sıfır anlamına gelmez. Satın alma, potansiyel müşteri ve link tıklaması ayrı sonuçlardır, toplanmaz.';

// Genel kırılım kartı: segment, harcama + pay, gösterim, tıklama, TO, satın alma, değer, ROAS (+ boş olmayan sonuç sütunları)
export function MetaBreakdownCard({ clientId, dim, title, sub, start, end, hideIfSingle = false, cur = 'TRY' }:
  { clientId: number; dim: string; title: string; sub?: string; start: string; end: string; hideIfSingle?: boolean; cur?: string }) {
  const { t } = useLang();
  const { d, err } = useBreakdown(clientId, dim, start, end);
  const rows = useMemo(() => (d?.satirlar || []).map((r: any) => ({ ...r, _label: segLabel(dim, r.etiket, t) })), [d, dim, t]);
  if (hideIfSingle && d && rows.filter((r: any) => (r.harcama_payi_pct || 0) >= 1).length <= 1) return null;
  const has = (k: string) => rows.some((r: any) => r[k] !== null && r[k] !== undefined);
  const maxShare = Math.max(1, ...rows.map((r: any) => r.harcama_payi_pct || 0));
  const cols: Col<any>[] = [
    { key: '_label', label: t('Segment'), render: r => <NameCell name={<span title={r.etiket}>{dim === 'creative_text' || dim === 'headline' ? `“${r._label}”` : r._label}</span>} /> },
    { key: 'harcama', label: t('Harcama'), align: 'right', render: r => <ShareCell text={money(r.harcama, cur)} share={((r.harcama_payi_pct || 0) / maxShare) * 100} /> },
    { key: 'gosterim', label: t('Gösterim'), align: 'right', mono: true, render: r => num(r.gosterim) },
    { key: 'tiklama', label: t('Tıklama'), align: 'right', mono: true, render: r => num(r.tiklama) },
    { key: 'tiklama_orani_pct', label: t('Tıklama oranı'), align: 'right', mono: true, render: r => pct(r.tiklama_orani_pct, 2) },
  ];
  if (dim === 'format' && has('reklam_sayisi')) cols.push({ key: 'reklam_sayisi', label: t('Reklam sayısı'), align: 'right', mono: true, render: r => num(r.reklam_sayisi) });
  if (has('link_tiklamasi')) cols.push({ key: 'link_tiklamasi', label: t('Link tıklaması'), align: 'right', mono: true, render: r => num(r.link_tiklamasi) });
  if (has('potansiyel_musteri')) cols.push({ key: 'potansiyel_musteri', label: t('Potansiyel müşteri'), align: 'right', mono: true, render: r => num(r.potansiyel_musteri) });
  const hasPurchase = has('satin_alma') || has('satin_alma_degeri');
  if (hasPurchase) cols.push(
    { key: 'satin_alma', label: t('Satın alma'), align: 'right', mono: true, render: r => num(r.satin_alma) },
    { key: 'satin_alma_degeri', label: t('Satın alma değeri'), align: 'right', mono: true, render: r => money(r.satin_alma_degeri, cur) },
    { key: 'roas', label: 'ROAS', align: 'right', mono: true, render: r => <span style={{ fontWeight: 800, color: roasColor(r.roas) }}>{roasTxt(r.roas)}</span> },
  );
  return (
    <Panel pad={false} title={t(title)} sub={sub ? t(sub) : undefined}>
      {err ? <div style={{ padding: '16px 20px', color: T.redText, fontSize: 13 }}>{t('Bu kırılım açılamadı.')}</div>
        : !d ? <Loading /> : (
        <>
          <DataTable rows={rows} cols={cols} initialSort="harcama" pageSize={10} minWidth={dim === 'creative_text' || dim === 'headline' ? 1000 : 880} />
          <div style={{ padding: '10px 20px 14px' }}><Muted>{!hasPurchase && rows.length > 0 && <>{t('Meta bu kırılımda satın alma verisi döndürmüyor; bu yüzden satın alma ve ROAS sütunları gösterilmiyor.')} </>}{t(NOTE)}</Muted></div>
        </>
      )}
    </Panel>
  );
}

// Yaş × cinsiyet ızgarası
export function MetaAgeGenderCard({ clientId, start, end, cur = 'TRY' }: { clientId: number; start: string; end: string; cur?: string }) {
  const { t } = useLang();
  const isMobile = useMobile();
  const { d, err } = useBreakdown(clientId, 'age_gender', start, end);
  const grid = useMemo(() => {
    const g: Record<string, Record<string, any>> = {};
    (d?.satirlar || []).forEach((r: any) => {
      const [age, gen] = String(r.etiket).split(' · ');
      (g[age] = g[age] || {})[gen] = r;
    });
    return g;
  }, [d]);
  const ages = Object.keys(grid).sort((x, y) => (/^unknown$/i.test(x) ? 1 : 0) - (/^unknown$/i.test(y) ? 1 : 0) || x.localeCompare(y));
  const gens = ['female', 'male', 'unknown'].filter(gn => ages.some(a => grid[a][gn]));
  const maxSpend = Math.max(1, ...((d?.satirlar || []).map((r: any) => r.harcama || 0)));
  return (
    <Panel title={t('Yaş ve Cinsiyet')} sub={t('Her hücre o yaş ve cinsiyet grubunun harcamasını ve ROAS’ını gösterir.')}>
      {err ? <div style={{ color: T.redText, fontSize: 13 }}>{t('Bu kırılım açılamadı.')}</div> : !d ? <Loading /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 6, width: '100%', minWidth: isMobile ? 420 : undefined }}>
            <thead><tr>
              <th style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: T.textDim, textAlign: 'left', padding: '0 6px' }}>{t('Yaş')}</th>
              {gens.map(gn => <th key={gn} style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: T.textDim, textAlign: 'left', padding: '0 6px' }}>{t(GENDER[gn])}</th>)}
            </tr></thead>
            <tbody>{ages.map(a => (
              <tr key={a}>
                <td style={{ fontSize: 12.5, fontWeight: 700, color: T.text, padding: '0 6px', whiteSpace: 'nowrap' }}>{a === 'Unknown' || a === 'unknown' ? t('Bilinmiyor') : a}</td>
                {gens.map(gn => {
                  const r = grid[a][gn];
                  const k = r ? (r.harcama || 0) / maxSpend : 0;
                  return (
                    <td key={gn} style={{ background: r ? `color-mix(in srgb, var(--py-accent) ${Math.round(6 + k * 34)}%, transparent)` : T.subtleBg, borderRadius: 10, padding: '10px 12px', verticalAlign: 'top' }}>
                      {r ? <>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: MONO, color: T.text }}>{money(r.harcama, cur, true)}</div>
                        <div style={{ fontSize: 11.5, marginTop: 3, color: T.textMidAlt }}>{r.satin_alma
                          ? <>ROAS <span style={{ fontWeight: 800, color: roasColor(r.roas), fontFamily: MONO }}>{roasTxt(r.roas)}</span> · {num(r.satin_alma)} {t('satın alma')}</>
                          : t('Satın alma yok')}</div>
                      </> : <span style={{ color: T.textDim, fontSize: 12 }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// Saatlik performans sayfası
const SHORT_DAY: Record<string, string> = { Pazartesi: 'Pzt', Salı: 'Sal', Çarşamba: 'Çar', Perşembe: 'Per', Cuma: 'Cum', Cumartesi: 'Cmt', Pazar: 'Paz' };
const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

export function MetaHourlyPage({ clientId, start, end, cur = 'TRY' }: { clientId: number; start: string; end: string; cur?: string }) {
  const { t } = useLang();
  const isMobile = useMobile();
  const [view, setView] = useState<'hour' | 'weekday'>('hour');
  const { d, err } = useBreakdown(clientId, view, start, end);
  const isDay = view === 'weekday';
  const rows = useMemo(() => (d?.satirlar || []).slice().sort((a: any, b: any) => isDay ? DAYS.indexOf(a.etiket) - DAYS.indexOf(b.etiket) : String(a.etiket).localeCompare(String(b.etiket))), [d, isDay]);
  const topSpend = useMemo(() => rows.slice().sort((a: any, b: any) => (b.harcama || 0) - (a.harcama || 0)).slice(0, 3), [rows]);
  const topRoas = useMemo(() => rows.filter((r: any) => (r.harcama || 0) >= 500 && r.roas).sort((a: any, b: any) => b.roas - a.roas).slice(0, 3), [rows]);
  const tile = (title: string, list: any[], hint?: string) => (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '16px 18px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: T.textDim }}>{t(title)}</div>
      <div style={{ display: 'flex', gap: 18, marginTop: 10, flexWrap: 'wrap' }}>
        {list.length ? list.map(r => (
          <div key={r.etiket}><div style={{ fontSize: 20, fontWeight: 800, fontFamily: MONO, color: T.text }}>{r.etiket}</div>
            <div style={{ fontSize: 11.5, color: T.textMidAlt }}>{money(r.harcama, cur, true)} · ROAS <span style={{ color: roasColor(r.roas), fontWeight: 800 }}>{roasTxt(r.roas)}</span></div></div>
        )) : <span style={{ color: T.textDim, fontSize: 13 }}>—</span>}
      </div>
      {hint && <div style={{ marginTop: 8 }}><Muted>{t(hint)}</Muted></div>}
    </div>
  );
  if (err) return <Panel><div style={{ color: T.redText, fontSize: 13 }}>{t('Bu kırılım açılamadı.')}</div></Panel>;
  if (!d) return <Panel><Loading /></Panel>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
        {tile(isDay ? 'Harcamanın En Yüksek Olduğu Günler' : 'Harcamanın En Yüksek Olduğu Saatler', topSpend)}
        {tile(isDay ? 'En İyi ROAS Günleri' : 'En İyi ROAS Saatleri', topRoas, isDay ? 'En az ₺500 harcanan günler arasında.' : 'En az ₺500 harcanan saatler arasında.')}
      </div>
      <Panel title={t(isDay ? 'Günlük Performans' : 'Saatlik Performans')} sub={t(isDay ? 'Seçili dönemdeki günler haftanın gününe göre toplanır.' : 'Saat, reklam hesabının saat dilimine göredir.')}
        right={<Segments value={view} onChange={v => setView(v as any)} items={[{ id: 'hour', label: t('Saatlik') }, { id: 'weekday', label: t('Günlük') }]} />}>
        <div style={{ height: isMobile ? 220 : 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--py-table-divider)" vertical={false} />
              <XAxis dataKey="etiket" tickFormatter={(v: string) => isDay ? (SHORT_DAY[v] || v.slice(0, 3)) : v.slice(0, 2)} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} axisLine={false} tickLine={false} interval={isMobile && !isDay ? 2 : 0} />
              <YAxis yAxisId="s" tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} axisLine={false} tickLine={false} width={56} tickFormatter={(v: number) => money(v, cur, true)} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} axisLine={false} tickLine={false} width={40} tickFormatter={(v: number) => `${v}×`} />
              <Tooltip formatter={(v: any, k: any) => k === 'harcama' ? [money(v, cur), t('Harcama')] : [roasTxt(v), 'ROAS']}
                contentStyle={{ background: 'var(--py-surface)', border: '1px solid var(--py-border)', borderRadius: 10, fontSize: 12 }} />
              <Bar yAxisId="s" dataKey="harcama" fill="#C9A84C" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Line yAxisId="r" dataKey="roas" stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 12, color: T.textMidAlt }}>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: '#C9A84C', marginRight: 6 }} />{t('Harcama')}</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 2, background: '#3b82f6', marginRight: 6, verticalAlign: 'middle' }} />ROAS</span>
        </div>
      </Panel>
      <MetaBreakdownTable rows={rows} cur={cur} isDay={isDay} />
    </div>
  );
}

function MetaBreakdownTable({ rows, cur, isDay }: { rows: any[]; cur: string; isDay: boolean }) {
  const { t } = useLang();
  const maxShare = Math.max(1, ...rows.map((r: any) => r.harcama_payi_pct || 0));
  return (
    <Panel pad={false}>
      <DataTable rows={rows} initialSort="etiket" initialDir={1} pageSize={24} minWidth={820} cols={[
        { key: 'etiket', label: t(isDay ? 'Gün' : 'Saat'), value: (r: any) => isDay ? DAYS.indexOf(r.etiket) : r.etiket, render: r => <span style={{ fontFamily: isDay ? undefined : MONO, fontWeight: 700 }}>{isDay ? t(r.etiket) : r.etiket}</span> },
        { key: 'harcama', label: t('Harcama'), align: 'right', render: r => <ShareCell text={money(r.harcama, cur)} share={((r.harcama_payi_pct || 0) / maxShare) * 100} /> },
        { key: 'gosterim', label: t('Gösterim'), align: 'right', mono: true, render: r => num(r.gosterim) },
        { key: 'tiklama', label: t('Tıklama'), align: 'right', mono: true, render: r => num(r.tiklama) },
        { key: 'tiklama_orani_pct', label: t('Tıklama oranı'), align: 'right', mono: true, render: r => pct(r.tiklama_orani_pct, 2) },
        { key: 'satin_alma', label: t('Satın alma'), align: 'right', mono: true, render: r => num(r.satin_alma) },
        { key: 'satin_alma_degeri', label: t('Satın alma değeri'), align: 'right', mono: true, render: r => money(r.satin_alma_degeri, cur) },
        { key: 'roas', label: 'ROAS', align: 'right', mono: true, render: r => <span style={{ fontWeight: 800, color: roasColor(r.roas) }}>{roasTxt(r.roas)}</span> },
      ]} />
      <div style={{ padding: '10px 20px 14px' }}><Muted>{t(NOTE)}</Muted></div>
    </Panel>
  );
}
